import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import { type FC } from 'react';
import styled, { css } from 'styled-components';

import {
  AIProducerCard,
  DesignCard,
  MediaCard,
  SidebarOption,
} from '../../types.ts/general';
import { useFlagsCombination } from '../../utility/useFlagsCombination';
import { useAiProducerCards } from './aiProducerCards';
import { useCompositionContext } from './CompositionContext';

const AI_PRODUCER_CARD_LABELS: Partial<Record<AIProducerCard, string>> = {
  [AIProducerCard.photo]: 'Photo',
  [AIProducerCard.music]: 'Music',
  [AIProducerCard.clip]: 'Clip',
  [AIProducerCard.scenes]: 'Scenes',
  [AIProducerCard.framing]: 'Framing',
};

/**
 * SidePanelTabs shows only the sub-menu cards (Photo/Video/Audio, etc.)
 * for the currently selected sidebar option.
 * The main navigation icons are now in SidebarIconMenu.
 */
const SidePanelTabs: FC = observer(() => {
  const {
    categories,
    selectedCategoryId,
    setSelectedCategoryId,
    setSelectedSubcategoryId: setSelectedSubcategory,
  } = useCompositionContext();
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const {
    creatorStudioEnableDesignTabs,
    creatorStudioEnableCompositionsTab,
    creatorStudioMediaEnableTab,
  } = useFlagsCombination(userIdentity.currentRole);
  const { cards: aiProducerCards, activeCard: activeAiProducerCard } =
    useAiProducerCards();
  const { sidebarOptions, mediaSubMenu, designSubMenu } = videoCreator;
  const isProducer = sidebarOptions === SidebarOption.aiProducer;
  const isMedia = sidebarOptions === SidebarOption.media;
  const isDesign = sidebarOptions === SidebarOption.design;

  const handleSetAICard = (state: AIProducerCard) => {
    videoCreator.aiProducerSubMenu = state;
    videoCreator.setActiveElements();
  };

  const handleSetMediaCard = (state: MediaCard) => {
    videoCreator.mediaSubMenu = state;
    videoCreator.setActiveElements();
  };

  const handleSetDesignCard = (state: DesignCard) => {
    videoCreator.designSubMenu = state;
    videoCreator.setActiveElements();
  };

  const ProducerCards: FC<{
    cards: AIProducerCard[];
    activeCard: AIProducerCard;
    onSetAICard: (card: AIProducerCard) => void;
  }> = ({ cards, activeCard, onSetAICard }) => (
    <Cards>
      {cards.map(card => (
        <Card
          key={card}
          onClick={() => onSetAICard(card)}
          isSelected={card === activeCard}
        >
          {AI_PRODUCER_CARD_LABELS[card]}
        </Card>
      ))}
    </Cards>
  );

  const MediaCards: FC<{
    mediaSubMenu: MediaCard;
    onSetMediaCard: (card: MediaCard) => void;
  }> = ({ mediaSubMenu, onSetMediaCard }) => (
    <Cards>
      <Card
        onClick={() => onSetMediaCard(MediaCard.photo)}
        isSelected={mediaSubMenu === MediaCard.photo}
      >
        Photo
      </Card>
      <Card
        onClick={() => onSetMediaCard(MediaCard.video)}
        isSelected={mediaSubMenu === MediaCard.video}
      >
        Video
      </Card>
      <Card
        onClick={() => onSetMediaCard(MediaCard.music)}
        isSelected={mediaSubMenu === MediaCard.music}
      >
        Audio
      </Card>
    </Cards>
  );

  const DesignCards: FC<{
    creatorStudioEnableDesignTabs: boolean;
    creatorStudioEnableCompositionsTab: boolean;
    designSubMenu: DesignCard;
    onSetDesignCard: (card: DesignCard) => void;
  }> = ({
    creatorStudioEnableDesignTabs,
    creatorStudioEnableCompositionsTab,
    designSubMenu,
    onSetDesignCard,
  }) => {
    if (!creatorStudioEnableDesignTabs) {
      return null;
    }

    const cards = [
      <Card
        key={DesignCard.text}
        onClick={() => {
          onSetDesignCard(DesignCard.text);
          setSelectedCategoryId(null);
          setSelectedSubcategory(null);
        }}
        isSelected={designSubMenu === DesignCard.text}
      >
        Text
      </Card>,
      <Card
        key={DesignCard.shape}
        onClick={() => {
          onSetDesignCard(DesignCard.shape);
          setSelectedCategoryId(null);
          setSelectedSubcategory(null);
        }}
        isSelected={designSubMenu === DesignCard.shape}
      >
        Shapes
      </Card>,
    ];

    if (creatorStudioEnableCompositionsTab) {
      categories.forEach(category => {
        cards.push(
          <Card
            key={category.id}
            onClick={() => {
              onSetDesignCard(DesignCard.composition);
              setSelectedCategoryId(category.id);
              setSelectedSubcategory(null);
            }}
            isSelected={selectedCategoryId === category.id}
          >
            {category.label}
          </Card>,
        );
      });
    }

    return <Cards>{cards}</Cards>;
  };

  // Hide the Media sub-tabs (Photo/Video/Audio) when an element is selected and
  // we're showing its element-specific settings with a breadcrumb header — that
  // covers video (primary + b-roll), image (logo + photo), and audio. The tabs
  // should only appear when browsing the Media library (no active element).
  const activeElement = videoCreator.getActiveElement();
  const isElementMediaSettings =
    !!activeElement &&
    (videoCreator.isVideoElement(activeElement) ||
      videoCreator.isImageElement(activeElement) ||
      activeElement.source.type === 'audio');

  // Only show tabs when there are sub-options
  const hasSubTabs =
    isProducer || (isMedia && !isElementMediaSettings) || isDesign;

  if (!hasSubTabs) {
    return null;
  }

  return (
    <Container>
      {isProducer && (
        <ProducerCards
          cards={aiProducerCards}
          activeCard={activeAiProducerCard}
          onSetAICard={handleSetAICard}
        />
      )}
      {isMedia && creatorStudioMediaEnableTab && (
        <MediaCards
          mediaSubMenu={mediaSubMenu}
          onSetMediaCard={handleSetMediaCard}
        />
      )}
      {isDesign && (
        <DesignCards
          creatorStudioEnableDesignTabs={creatorStudioEnableDesignTabs}
          creatorStudioEnableCompositionsTab={
            creatorStudioEnableCompositionsTab
          }
          designSubMenu={designSubMenu}
          onSetDesignCard={handleSetDesignCard}
        />
      )}
    </Container>
  );
});

export default SidePanelTabs;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Cards = styled.nav`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 0 var(--spacing-md);
  align-items: center;
  border-bottom: 1px solid var(--border-subtle);
  padding: var(--spacing-sm) 0 0;
  white-space: nowrap;
`;

const Card = styled.div<{ isSelected?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: var(--spacing-sm) 0;
  margin: 0;
  cursor: pointer;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-sm);
  color: var(--text-secondary);
  white-space: nowrap;
  transition: color var(--transition-fast);
  border: none;
  background: none;

  &:hover {
    color: var(--foreground);
  }

  ${props =>
    props.isSelected &&
    css`
      color: var(--foreground);
      font-weight: var(--font-weight-medium);
      &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        height: var(--spacing-2xs);
        background-color: var(--accent);
        border-radius: 1px;
      }
    `}
`;
